// Agent service following SRP, SOC, and DRY principles
// Uses AgentApiClient for all agent-related operations

import { AgentApiClient } from "@/lib/api/clients/agent-api-client";
import { AppBaseService as BaseService } from "./base/app-base-service";
import {
  ServiceResult,
  IEntityService,
  PaginationParams,
} from "./base/service-types";
import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
} from "@/lib/types/api/agent-types";
import { PaginatedResponse } from "@/lib/types/api/common-types";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

/**
 * agent service for handling agent operations
 * Follows SRP: Single responsibility for agent operations
 * Follows SOC: Separates agent business logic from HTTP concerns and UI concerns
 * Follows DRY: Uses enhanced base service patterns
 */
export class AgentService
  extends BaseService
  implements IEntityService<Agent, CreateAgentRequest, UpdateAgentRequest>
{
  readonly serviceName = "AgentService";

  constructor(private agentApiClient: AgentApiClient = new AgentApiClient()) {
    super();
  }

  /**
   * Create a new agent
   * @param data - Agent creation data
   * @returns Promise<ServiceResult<Agent>>
   */
  async create(data: CreateAgentRequest): Promise<ServiceResult<Agent>> {
    this.validateRequired(
      data as unknown as Record<string, unknown>,
      ["name"],
      {
        operation: "createAgent",
        entity: "agent",
      }
    );

    return this.executeWithErrorHandling(
      () => this.agentApiClient.createAgent(data),
      {
        operation: "createAgent",
        entity: "agent",
        metadata: { name: data.name },
      }
    );
  }

  /**
   * Get agent by ID
   * @param id - Agent ID
   * @returns Promise<ServiceResult<Agent>>
   */
  async getById(id: string): Promise<ServiceResult<Agent>> {
    this.validateRequired({ id }, ["id"], {
      operation: "getAgent",
      entity: "agent",
    });

    return this.executeWithErrorHandling(
      () => this.agentApiClient.getAgent(id),
      {
        operation: "getAgent",
        entity: "agent",
        entityId: id,
      }
    );
  }

  /**
   * Update agent
   * @param id - Agent ID
   * @param data - Agent update data
   * @returns Promise<ServiceResult<Agent>>
   */
  async update(
    id: string,
    data: UpdateAgentRequest
  ): Promise<ServiceResult<Agent>> {
    this.validateRequired({ id }, ["id"], {
      operation: "updateAgent",
      entity: "agent",
    });

    return this.executeWithErrorHandling(
      () => this.agentApiClient.updateAgent(id, data),
      {
        operation: "updateAgent",
        entity: "agent",
        entityId: id,
        metadata: { fieldsUpdated: Object.keys(data) },
      }
    );
  }

  /**
   * Delete agent with comprehensive error handling
   * @param id - Agent ID
   * @returns Promise<ServiceResult<void>>
   */
  async delete(id: string): Promise<ServiceResult<void>> {
    this.validateRequired({ id }, ["id"], {
      operation: "deleteAgent",
      entity: "agent",
    });

    return this.executeWithErrorHandling(
      () => this.agentApiClient.deleteAgent(id),
      {
        operation: "deleteAgent",
        entity: "agent",
        entityId: id,
      }
    );
  }

  /**
   * List agents with pagination
   * @param params - Pagination parameters
   * @returns Promise<ServiceResult<PaginatedResponse<Agent>>>
   */
  async list(
    params?: PaginationParams & { access?: "any" | "chat" }
  ): Promise<ServiceResult<PaginatedResponse<Agent>>> {
    const paginationParams = this.createPaginationParams(
      params?.skip,
      params?.limit
    );

    return this.executeWithErrorHandling(
      () =>
        this.agentApiClient.listAgents({
          ...paginationParams,
          access: params?.access,
        }),
      {
        operation: "listAgents",
        entity: "agent",
        metadata: { pagination: paginationParams },
      }
    );
  }

  /**
   * Get default agent
   * @returns Promise<ServiceResult<Agent>>
   */
  async getDefaultAgent(): Promise<ServiceResult<Agent>> {
    return this.executeWithErrorHandling(
      () => this.agentApiClient.getDefaultAgent(),
      {
        operation: "getDefaultAgent",
        entity: "agent",
      }
    );
  }

  /**
   * deletion with navigation handling
   * @param agentId - Agent ID
   * @param router - Next.js router instance for navigation
   * @param currentPathname - Current pathname to determine redirect behavior
   * @returns Promise<ServiceResult<void>>
   */
  async deleteWithNavigation(
    agentId: string,
    router: AppRouterInstance,
    currentPathname: string
  ): Promise<ServiceResult<void>> {
    const result = await this.delete(agentId);

    if (result.success) {
      this.handlePostDeleteRedirect(agentId, router, currentPathname);
    }

    return result;
  }

  /**
   * Validate agent deletion eligibility
   * @param agent - The agent to validate
   * @param userId - The current user ID
   * @returns Promise<{ canDelete: boolean; reasons: string[] }>
   */
  async validateDeletionEligibility(
    agent: Agent | null
  ): Promise<{ canDelete: boolean; reasons: string[] }> {
    const reasons: string[] = [];

    if (!agent) {
      reasons.push("Agent not found");
      return { canDelete: false, reasons };
    }

    // Prevent deletion of default agent. Authority to delete is enforced by the
    // backend authorization engine (owner-only agent.delete).
    if (agent.is_default_agent) {
      reasons.push("Cannot delete the default agent");
    }

    return {
      canDelete: reasons.length === 0,
      reasons,
    };
  }

  /**
   * Get agent deletion impact assessment
   * @param agentId - The agent ID
   * @param sessionsCount - Number of sessions (from UI state)
   * @returns Promise<DeletionImpact>
   */
  async assessDeletionImpact(
    agentId: string,
    sessionsCount: number = 0
  ): Promise<{
    estimatedDataToDelete: string[];
    affectedSessions: number;
    warningMessage?: string;
  }> {
    const estimatedDataToDelete = [
      "Agent configuration and settings",
      "All uploaded files and documents",
      "Chat sessions and message history",
      "Agent permissions and member access",
    ];

    const warningMessage =
      sessionsCount > 0
        ? `This will delete ${sessionsCount} chat session(s) permanently.`
        : undefined;

    return {
      estimatedDataToDelete,
      affectedSessions: sessionsCount,
      warningMessage,
    };
  }

  /**
   * Handle redirect logic after successful agent deletion
   * @param agentId - The deleted agent ID
   * @param router - Next.js router instance
   * @param currentPathname - Current pathname
   */
  private handlePostDeleteRedirect(
    agentId: string,
    router: AppRouterInstance,
    currentPathname: string
  ): void {
    // Check if we're currently in a session for the deleted agent
    const isInAgentSession = currentPathname.includes(
      `/agents/${agentId}/sessions/`
    );

    // Check if we're on any page of the deleted agent
    const isOnAgentPage = currentPathname.includes(`/agents/${agentId}`);

    // Check if we're already on the agents list page
    const isOnAgentsPage =
      currentPathname === "/agents" || currentPathname.endsWith("/agents");

    if (isInAgentSession || isOnAgentPage) {
      // User was viewing the deleted agent, redirect to agents list
      router.push("/agents");
    } else if (isOnAgentsPage) {
      // User was on agents page (e.g., deleted from sidebar), refresh the page
      router.refresh();
    } else {
      // User was somewhere else, just redirect to agents list for safety
      router.push("/agents");
    }
  }
}

/**
 * Default singleton instance for ease of use
 * Can be overridden with dependency injection in tests
 */
export const enhancedAgentService = new AgentService();
